import { useState, useEffect, useRef, useContext } from 'react';
import { IconButton } from '@teo/components';
import {
  CrossFilled,
  Document,
  Information,
  Chapter,
} from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import ExtraInfoEmpty from './ExtraInfoEmpty';
import { axios } from '@/axios';
import { config } from '@/config';
import { uploadLesson, uploadImage, uploadFile } from '@/query/documents';
import ExtraInfoList from './ExtraInfoList';
import { DuplicateLibrary } from '@/components/DuplicateLessons/DuplicateLibrary';
import { useMediaQuery } from 'react-responsive';
import { LibraryModal } from '../LibraryModal/LibraryModal';
import { VideoUploadModal } from '../VideoUploadModal/VideoUploadModal';
import { newUid, uploadFile as uploadFileSelect } from '@/util';
import { encode } from '@/base64';
import { VideoCameraModal } from '@/components/CameraModal/VideoCameraModal';
import { AiDocumentJobModal } from '@/components/AiDocumentJobModal';
import { AuthContext } from '@/components/Contexts/AuthContext';

function parseUploadUrl(uploadUrl) {
  // ex: https://.../instruction_uploads/<uid>/camera.webm
  const url = new URL(uploadUrl);
  const parts = url.pathname.split('/').filter(Boolean);
  const filename = parts.pop() || ''; // "camera.webm"
  const uid = parts.pop() || ''; // "<uid>"
  const base = filename.replace(/\.[^.]+$/, ''); // "camera"
  const cdn = `${url.protocol}//${url.host}`;

  const statusUrl = `${cdn}/instruction_transcripts/${uid}/${base}.status.json`;
  const stepsUrl = `${cdn}/instruction_transcripts/${uid}/${base}.steps.json`;
  return { uid, base, statusUrl, stepsUrl };
}

function normalizeCourseLang(lang) {
  const value = String(lang || '').trim();

  if (!value) return null;

  if (/^en[-_]gb$/i.test(value)) return 'en-GB';
  if (/^en[-_]us$/i.test(value)) return 'en-US';
  return value.split(/[-_]/)[0].toLowerCase();
}

function getCourseLangFromExtraInfoContext({
  publication,
  activeNode,
  node,
  data,
}) {
  const candidates = [
    publication?.courseLang,
    publication?.course_lang,
    publication?.coursecontentLang,
    publication?.courseContentLang,
    publication?.publicationLang,
    publication?.publicationLanguage,
    publication?.lang,
    publication?.language,
    publication?.course?.lang,
    publication?.course?.language,
    publication?.coursecontent?.lang,
    publication?.coursecontent?.language,
    publication?.courseContent?.lang,
    publication?.courseContent?.language,
    activeNode?.data?.courseLang,
    activeNode?.data?.course_lang,
    activeNode?.data?.coursecontentLang,
    activeNode?.data?.courseContentLang,
    activeNode?.data?.publicationLang,
    activeNode?.data?.publicationLanguage,
    node?.courseLang,
    node?.course_lang,
    node?.coursecontentLang,
    node?.courseContentLang,
    node?.publicationLang,
    node?.publicationLanguage,
    node?.data?.courseLang,
    node?.data?.course_lang,
    node?.data?.coursecontentLang,
    node?.data?.courseContentLang,
    node?.data?.publicationLang,
    node?.data?.publicationLanguage,
    data?.courseLang,
    data?.course_lang,
    data?.coursecontentLang,
    data?.courseContentLang,
    data?.publicationLang,
    data?.publicationLanguage,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCourseLang(candidate);
    if (normalized) return normalized;
  }

  return null;
}

const ExtraInfo = ({
  setShowExtraInfo,
  activeNode,
  node,
  publication,
  prevTree,
  setTree,
  setAddLesson,
  setLessons,
  nameItem,
  extraInfoLessons,
  setActiveNode,
  setParentNode,
  setMobileSidebar,
  setExtraInfoEdit,
  extraInfoEdit,
  isFroalaLoaded,
}) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const canUseCourseAndLessonAi = Boolean(
    auth?.user?.useTeoAiCourseAndLessonGeneration
  );
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  const fileInputRef = useRef(null);

  const [isHomePage, setIsHomePage] = useState(false);
  const [newExtraInfo, setNewExtraInfo] = useState();
  const [newAsset, setNewAsset] = useState();
  const [courseContentId, setCourseContentId] = useState();
  const { droppable, data } = node;
  const [nodeId, setNodeId] = useState();
  const [nodePath, setNodePath] = useState(null);
  const [isOpenLibrary, setIsOpenLibrary] = useState(false);
  const [extraInfoTree, setExtraInfoTree] = useState(null);
  const [isOpenLibraryModal, setIsOpenLibraryModal] = useState(false);
  const [newActiveExtra, setNewActiveExtra] = useState(null);
  const [isAddButtonBottom, setIsAddButtonBottom] = useState(false);
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);
  const [isOpenAiExtraInfoModal, setIsOpenAiExtraInfoModal] = useState(false);

  const [isOpenRecord, setIsOpenRecord] = useState(false);

  const aiExtraInfoCourseLang = getCourseLangFromExtraInfoContext({
    publication,
    activeNode,
    node,
    data,
  });

  useEffect(() => {
    if (node?.path === '/') {
      setIsHomePage(true);
      setCourseContentId(node?.coursecontentId);
      setNodeId(node?.id);
      setNodePath(null);
    } else {
      setIsHomePage(false);
      setCourseContentId(data?.coursecontentId);
      setNodeId(data?.lessonId ? data?.lessonId : data?.id);
      setNodePath(data?.path);
    }
  }, [node, data]);

  const handleScroll = () => {
    const scrollSection = document.getElementById('extra-info');
    const scrollTreeSection = document.getElementById('extra-info-tree');
    const scrollY = window.scrollY;
    if (scrollSection) {
      if (isMobile) {
        if (scrollY < 66) {
          scrollSection.style.marginTop = 65 - scrollY + 'px';
          const heightTop = 65 - scrollY + 53;
          scrollTreeSection.style.maxHeight = `calc(100vh - ${heightTop}px)`;
        } else {
          scrollSection.style.marginTop = '0px';
          scrollTreeSection.style.maxHeight = `calc(100vh - 53px)`;
        }
      } else {
        if (scrollY < 81) {
          scrollSection.style.marginTop = 80 - scrollY + 'px';
          const heightTop = 80 - scrollY + 53;
          scrollTreeSection.style.maxHeight = `calc(100vh - ${heightTop}px)`;
        } else {
          scrollSection.style.marginTop = '0px';
          scrollTreeSection.style.maxHeight = `calc(100vh - 53px)`;
        }
      }
    }
  };

  useEffect(() => {
    if (!extraInfoEdit) {
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const checkPositionAddButton = () => {
    const blockTree = document.getElementById('course-lessons');
    const contentTree = extraInfoEdit
      ? document.getElementById('extra-info')
      : document.getElementById('extra-lessons-list');
    if (blockTree && contentTree) {
      const blockTreeHeight = blockTree.offsetHeight;
      const contentTreeHeight = contentTree.offsetHeight;
      const space = extraInfoEdit ? 220 : 240;
      if (blockTreeHeight > contentTreeHeight + space) {
        setIsAddButtonBottom(false);
      } else {
        setIsAddButtonBottom(true);
      }
    }
  };

  useEffect(() => {
    if (newActiveExtra && extraInfoTree && extraInfoEdit) {
      const newNode = extraInfoTree?.find((el) => el?.id === newActiveExtra);
      setActiveNode(newNode);
      window.scrollTo(0, 0);
    }
    checkPositionAddButton();
  }, [extraInfoTree]);

  const getExtraInfoRootDirectoryId = () =>
    isHomePage ? nodeId : droppable ? nodeId : null;

  const getExtraInfoRootLessonId = () =>
    !isHomePage && !droppable ? nodeId : null;

  const getNextExtraInfoYindex = () => {
    if (extraInfoTree?.length) {
      return (
        extraInfoTree?.reduce((max, obj) => {
          return obj?.data?.yindex > max ? obj?.data?.yindex : max;
        }, extraInfoTree[0]?.data?.yindex) + 1
      );
    }

    return 0;
  };

  const unwrapAiLessonResult = (result) => {
    return (
      result?.result || result?.data?.result || result?.data || result || {}
    );
  };

  const getAiCreatedExtraInfoIds = (result) => {
    const resultData = unwrapAiLessonResult(result);

    return {
      data: resultData,
      lessonId: Number(
        resultData?.lessonId ||
          resultData?.lesson?.id ||
          resultData?.use?.lessonId ||
          resultData?.usedLesson?.lessonId ||
          0
      ),
      usesId: Number(
        resultData?.usesId ||
          resultData?.useId ||
          resultData?.use?.id ||
          resultData?.usedLesson?.id ||
          0
      ),
    };
  };

  const refreshExtraInfoAfterAiLesson = async (result) => {
    const { lessonId, usesId } = getAiCreatedExtraInfoIds(result);

    if (!usesId) {
      console.warn('AI extra info lesson job completed without usesId', result);
      setTree(null);
      return null;
    }

    setNewActiveExtra(usesId);

    try {
      const parsedCourseContentId = parseInt(courseContentId, 10);
      const response = await axios.get(`${config.backend}/usedlessonassets`, {
        params: { coursecontentId: parsedCourseContentId },
      });
      const nextLessons = Array.isArray(response?.data) ? response.data : [];

      setLessons(nextLessons);
    } catch (err) {
      console.error(
        'Failed to refresh extra info after AI lesson creation',
        err
      );
    }

    // Force the tree/list to rebuild from the refreshed canonical backend data.
    // Do not call setAddLesson here: that is an optimistic insert path for
    // manual creation and duplicates the already-refreshed AI lesson until reload.
    setTree(null);

    return { lessonId, usesId };
  };

  const goToAiCreatedExtraInfoLesson = (result) => {
    const { usesId } = getAiCreatedExtraInfoIds(result);

    if (usesId) {
      setNewActiveExtra(usesId);
    }
  };

  const handleStateLesson = async (state) => {
    const content = `<div style="position:relative" contenteditable=false class="widget-placeholder fr-deletable" id="${
      state.uid
    }"><div data-open="SOW" data-state="${encode(
      JSON.stringify(state)
    )}"><div data-closed="EOW"></div></div></div>`;

    const title =
      state.title ||
      t('pages.create_edit_course.new_lesson_button') ||
      'New lesson';

    return handleNewExtraIndo(title, content, 'instruction');
  };

  const handleNewExtraIndo = async (
    name = t('extra_info.title'),
    content = '',
    lessonType = 'theory'
  ) => {
    try {
      const { data: lesson } = await axios.post(`${config.backend}/lessons`, {
        name: name,
        canEdit: true,
        content: '',
        description: '',
        lessonType: lessonType,
      });

      // Store the content alongside the lesson so the effect can upload it
      setNewExtraInfo({ ...lesson, _pendingContent: content });

      return lesson.id;
    } catch (err) {
      console.error('🔥 Failed to create lesson:', err);
      throw err;
    }
    // const data = await axios.post(`${config.backend}/lessons`, {
    //   name: name,
    //   canEdit: true,
    //   content: '',
    //   description: '',
    //   lessonType: lessonType,
    // });
    // setNewExtraInfo(data?.data);
  };

  const createLesson = async () => {
    const result = await axios.post(`${config.backend}/uses`, {
      canEdit: true,
      coursecontentId: parseInt(courseContentId, 10),
      lessonId: newExtraInfo?.id,
    });

    const createExtraInfoId = result?.data?.lessonId;
    const createExtraInfoUsesId = result?.data?.id;

    let newYindex;
    if (extraInfoTree?.length) {
      newYindex =
        extraInfoTree?.reduce((max, obj) => {
          return obj?.data?.yindex > max ? obj?.data?.yindex : max;
        }, extraInfoTree[0]?.data?.yindex) + 1;
    } else {
      newYindex = 0;
    }
    axios.put(`${config.backend}/use/${result?.data?.id}`, {
      coursecontentId: parseInt(courseContentId, 10),
      lessonId: createExtraInfoId,
      onlyTheory: true,
      rootDirectoryId: isHomePage ? nodeId : droppable ? nodeId : null,
      rootLessonId: !isHomePage && !droppable ? nodeId : null,
      path: data?.path
        ? `${data?.path}/${createExtraInfoId}`
        : `/${createExtraInfoId}`,
      yindex: newYindex,
    });
    setAddLesson({
      ...newExtraInfo,
      canEdit: true,
      coursecontentId: parseInt(courseContentId, 10),
      onlyTheory: true,
      lessonId: createExtraInfoId,
      rootDirectoryId: isHomePage ? nodeId : droppable ? nodeId : null,
      rootLessonId: !isHomePage && !droppable ? nodeId : null,
      path: node?.data?.path
        ? `${node?.data?.path}/${createExtraInfoId}`
        : `/${createExtraInfoId}`,
      usesId: createExtraInfoUsesId,
      yindex: newYindex,
    });
    setNewActiveExtra(createExtraInfoUsesId);
    setTree(null);
    setNewExtraInfo(null);
  };

  useEffect(() => {
    if (!newExtraInfo?.id) return;

    const content = newExtraInfo._pendingContent ?? '';

    uploadLesson(newExtraInfo.postData, content)
      .then(() => {
        createLesson();
      })
      .catch((err) => {
        console.error('❌ Upload failed for lesson', newExtraInfo.id, err);
      });

    // if (newExtraInfo?.id) {
    //   uploadLesson(newExtraInfo?.postData, '');
    //   createLesson();
    // }
  }, [newExtraInfo]);

  const handleDelete = (les) => {
    axios.delete(`${config.backend}/use/${les?.data?.usesId}`);

    if (les?.id) {
      setLessons((prev) =>
        prev?.filter((el) => {
          return el?.usesId !== les?.id;
        })
      );
    }
  };

  useEffect(() => {
    if (newAsset) {
      createImageInfo();
    }
  }, [newAsset]);

  const createImageInfo = async () => {
    const result = await axios.post(`${config.backend}/uses`, {
      coursecontentId: parseInt(courseContentId, 10),
      assetId: newAsset?.id,
    });

    const createExtraInfoImageId = result?.data?.assetId;
    const createExtraInfoImageUsesId = result?.data?.id;

    if (createExtraInfoImageId) {
      let newYindex;
      if (extraInfoTree?.length) {
        newYindex =
          extraInfoTree?.reduce((max, obj) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, extraInfoTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/use/${result?.data?.id}`, {
        assetId: createExtraInfoImageId,
        canEdit: true,
        coursecontentId: parseInt(courseContentId, 10),
        onlyTheory: true,
        rootDirectoryId: isHomePage ? nodeId : droppable ? nodeId : null,
        rootLessonId: !isHomePage && !droppable ? nodeId : null,
        path: data?.path
          ? `${data?.path}/${createExtraInfoImageId}`
          : `/${createExtraInfoImageId}`,
        yindex: newYindex,
      });
      setAddLesson({
        ...newAsset,
        canEdit: true,
        coursecontentId: parseInt(courseContentId, 10),
        onlyTheory: true,
        assetId: createExtraInfoImageId,
        rootDirectoryId: isHomePage ? nodeId : droppable ? nodeId : null,
        rootLessonId: !isHomePage && !droppable ? nodeId : null,
        path: node?.data?.path
          ? `${node?.data?.path}/${createExtraInfoImageId}`
          : `/${createExtraInfoImageId}`,
        usesId: createExtraInfoImageUsesId,
        yindex: newYindex,
      });
      setNewActiveExtra(createExtraInfoImageUsesId);
      setTree(null);
      setNewAsset(null);
    }
  };

  const handleUploadImage = async (event) => {
    const img = event.target.files[0];
    const size = 900;
    const data = await uploadImage(img, size, size);

    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: data,
    });
    setNewAsset(dataImage?.data);
  };

  const handleUploadImageLibrary = async (image) => {
    const res = await fetch(image);
    const blob = await res.blob();
    const mime = blob.type;
    const ext = mime.slice(mime.lastIndexOf('/') + 1, mime.length);
    const img = new File([blob], `${blob.size}.${ext}`, {
      type: mime,
    });
    const size = 900;
    const data = await uploadImage(img, size, size);

    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: data,
    });
    setNewAsset(dataImage?.data);
  };

  const handleUseImageLibrary = async (imageUrl) => {
    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: imageUrl,
    });
    setNewAsset(dataImage?.data);
  };

  const handleOpenLibrary = () => {
    setIsOpenLibrary(true);
  };

  const handleClose = async () => {
    if (extraInfoEdit) {
      setActiveNode(isHomePage ? null : node);
      setExtraInfoEdit(false);
    } else {
      setShowExtraInfo(false);
    }
  };

  function startInstruction() {
    const widgetState = {
      uid: newUid(10),
      type: 'Instruction',
      titleWidget: 'Instruction',
      noSubmit: true,
      url: null,
      title: t('pages.create_edit_course.video_instruction.new_instructional'),
      statusUrl: null,
      stepsUrl: null,
      steps: [],
    };

    handleStateLesson(widgetState);
  }

  function startInstructionUpload(file) {
    if (!file) return;

    (async () => {
      try {
        // Step 1: reserve upload & get final public URL,
        // but DO NOT wait for the binary upload (fire-and-forget)
        const uploadUrl = await uploadFile('/instruction_uploads', file, {
          fireAndForget: true,
        });

        // Derive uid + status/steps URLs from the final path
        const { uid, base, statusUrl, stepsUrl } = parseUploadUrl(uploadUrl);

        // Initial widget state: no steps yet, but we know where status & steps will appear
        const widgetState = {
          uid,
          type: 'Instruction',
          titleWidget: 'Instruction',
          noSubmit: true,
          url: uploadUrl,
          title: t('pages.create_edit_course.new_instruction_title'),
          statusUrl,
          stepsUrl,
          steps: [],
        };

        // Create the lesson immediately with the widget in it
        await handleStateLesson(widgetState);
      } catch (err) {
        console.error('Failed to upload instruction video', err);
      }
    })();
  }

  return (
    <div
      className={`${
        !extraInfoEdit && 'fixed inset-0 z-[10] flex bg-black/50 '
      } ${!extraInfoEdit ? (isMobile ? ' mt-[60px]' : ' mt-[80px]') : ' '}`}
      id="extra-info"
    >
      {!extraInfoEdit && (
        <div className="flex-auto" onClick={() => handleClose()}></div>
      )}
      <div
        className={`${
          !extraInfoEdit && 'h-full w-full max-w-[380px] bg-white '
        } flex  flex-col 
        ${!extraInfoEdit ? (isMobile ? ' max-w-full' : ' max-w-[380px]') : ' '}
        `}
      >
        <div className="flex items-center border-b py-2 pl-6 pr-2">
          <h4>{t('extra_info.title')}</h4>
          <IconButton
            name="close_extra_info"
            className="my-auto ml-auto"
            Icon={CrossFilled}
            variant="ghost"
            onClick={() => {
              handleClose();
            }}
          />
        </div>
        <div
          className={`flex h-full flex-col p-3 ${
            !extraInfoEdit && 'overflow-y-auto '
          }`}
          id="extra-info-tree"
        >
          <div className="flex items-center">
            {isHomePage ? (
              <Information className="m-2 w-6 min-w-6 text-grey-07" />
            ) : droppable ? (
              <Chapter className="m-2 w-6 min-w-6 text-grey-07" />
            ) : (
              <Document className="m-2 w-6 min-w-6 text-grey-07" />
            )}

            <h4>{nameItem}</h4>
          </div>
          {extraInfoLessons?.length > 0 ? (
            <ExtraInfoList
              activeNode={activeNode}
              extraInfoLessons={extraInfoLessons}
              handleNewExtraIndo={handleNewExtraIndo}
              handleDelete={handleDelete}
              setLessons={setLessons}
              setAddLesson={setAddLesson}
              parentNode={node}
              handleOpenLibrary={handleOpenLibrary}
              setExtraInfoTree={setExtraInfoTree}
              extraInfoTree={extraInfoTree}
              setActiveNode={setActiveNode}
              setParentNode={setParentNode}
              setMobileSidebar={setMobileSidebar}
              setExtraInfoEdit={setExtraInfoEdit}
              extraInfoEdit={extraInfoEdit}
              setShowExtraInfo={setShowExtraInfo}
              isMobile={isMobile}
              fileInputRef={fileInputRef}
              setIsOpenVideoModal={setIsOpenVideoModal}
              setIsOpenLibraryModal={setIsOpenLibraryModal}
              setNewActiveExtra={setNewActiveExtra}
              newActiveExtra={newActiveExtra}
              isAddButtonBottom={isAddButtonBottom}
              isFroalaLoaded={isFroalaLoaded}
              startInstruction={startInstruction}
              startAiExtraInfoLesson={
                canUseCourseAndLessonAi
                  ? () => setIsOpenAiExtraInfoModal(true)
                  : undefined
              }
            />
          ) : (
            <ExtraInfoEmpty
              handleNewExtraIndo={handleNewExtraIndo}
              handleOpenLibrary={handleOpenLibrary}
              fileInputRef={fileInputRef}
              setIsOpenVideoModal={setIsOpenVideoModal}
              setIsOpenLibraryModal={setIsOpenLibraryModal}
              startInstruction={startInstruction}
              startAiExtraInfoLesson={
                canUseCourseAndLessonAi
                  ? () => setIsOpenAiExtraInfoModal(true)
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {canUseCourseAndLessonAi && (
        <AiDocumentJobModal
          open={isOpenAiExtraInfoModal}
          mode="lesson"
          coursecontentId={courseContentId}
          extraPayload={{
            ...(aiExtraInfoCourseLang ? { lang: aiExtraInfoCourseLang } : {}),
            extraInfo: true,
            onlyTheory: true,
            rootDirectoryId: getExtraInfoRootDirectoryId(),
            rootLessonId: getExtraInfoRootLessonId(),
            parentPath: nodePath,
            yindex: getNextExtraInfoYindex(),
          }}
          onClose={() => setIsOpenAiExtraInfoModal(false)}
          onJobCompleted={refreshExtraInfoAfterAiLesson}
          onCompleted={goToAiCreatedExtraInfoLesson}
          title={t('pages.ai_document_job_modal.create_extra_info_title')}
          description={t(
            'pages.ai_document_job_modal.create_extra_info_description'
          )}
          promptPlaceholder={t(
            'pages.ai_document_job_modal.extra_info_prompt_placeholder'
          )}
          startLabel={t('pages.ai_document_job_modal.start_extra_info_job')}
          completedActionLabel={t(
            'pages.ai_document_job_modal.open_extra_info'
          )}
        />
      )}

      {isOpenLibrary && (
        <DuplicateLibrary
          closeModal={() => setIsOpenLibrary(false)}
          coursecontentId={courseContentId}
          prevTree={prevTree}
          setAddLesson={setAddLesson}
          // setAsset={setAsset}
          setIsOpenLibrary={setIsOpenLibrary}
          parentPath={nodePath}
          parentId={nodeId}
          setTree={setTree}
          extraInfo={true}
          isHomePage={isHomePage}
          droppable={droppable}
          extraInfoTree={extraInfoTree}
          publication={publication}
        />
      )}

      {isOpenLibraryModal && (
        <LibraryModal
          setIsOpenLibraryModal={setIsOpenLibraryModal}
          isOpenLibraryModal={isOpenLibraryModal}
          lessonPage={false}
          setUploadFile={handleUploadImageLibrary}
          onSelectImage={handleUseImageLibrary}
        />
      )}

      {isOpenVideoModal && (
        <VideoUploadModal
          setIsOpenVideoModal={setIsOpenVideoModal}
          isOpenVideoModal={isOpenVideoModal}
          setNewAsset={setNewAsset}
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleUploadImage}
        accept="image/*"
        style={{ display: 'none' }}
        multiple={false}
      />

      {isOpenRecord && (
        <VideoCameraModal
          forceSound={true}
          setIsOpenVideoModal={setIsOpenRecord}
          isOpenVideoModal={isOpenRecord}
          setUploadFile={(resultFn) => {
            const result = resultFn([]);
            if (!result || !result[0]) return;
            const file = new File(
              result.map((x) => x.blob),
              `camera.${result[0].mimeType}`,
              { type: result[0].type }
            );
            startInstructionUpload(file);
          }}
        />
      )}
    </div>
  );
};

export default ExtraInfo;
